import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";

import useVoucherDetails from "../../../hooks/vouchers/useVoucherDetails";


// Material UI
import Grid from "@mui/material/Grid";
import Masonry from '@mui/lab/Masonry';

// Hooks
import useOrder from "../../../hooks/orders/useOrder";
import useBranches from "../../../hooks/branch/useBranches";
import useNotify from "../../../hooks/notify";
import useUser from "../../../hooks/user/useUser";
import useCashRegisterById from "../../../hooks/cashRegisters/useCashRegisterById";
import useWarehouse from '../../../hooks/warehouse/useWarehouse';
import orderService from '../../../services/orderService';
import useAuthorize from '../../../hooks/user/useAuthorize';

// Components
import NewSaleModal from "../create";
import RefundModal from "../refund";
import ModalManualConfirm from '../../../components/modal/manualInvoice';
import TerminalQrPvModal from "../../../components/modal/terminalQrPvModal";
import TerminalPvModal from "../../../components/modal/terminalPvModal";
import customerService from '../../../services/customerService';

// Details
import TitleDetail from "./title";
import ShippingDetails from "./shippingDetails";
import PaymentDetails from "./paymentDetails";
import RelatedOrderDetail from "./relatedOrderDetail";
import ObservationsDetails from "./observationsDetails";
// import VoucherDetails from "./voucherDetails";
import TotalsDetail from "./totalsDetail";

import {
  GeneralDetail,
  Spinner,
  ProductsDetail,
  PromotionsDetail,
  PromoAddition,
  InternalVoucherDetails,
  CardContact,
  VoucherDetails,
} from '@arredo/reactjs-ui'

// Others
import { subChannels } from "../../../models/Channels";
import { HasPendingTerminalItems, onRedirectToRefundView, OrderStates } from "../../../models/OrderEntity";
import { PaymentStates } from "../../../models/PaymentStates";
import { paymentMethods } from "../../../models/PaymentMethods";
import ExtraDataModal from "../../../components/modal/extraData";
import { getCardPaymentsToRefundFromOrder, getTerminalPvBlocker, filterCardPaymentsByRefundAmount } from "../../../utils/payment-utils";
import { Permissions } from "../../../models/Roles";


const DetailView = () => {
  const isAuthorized = useAuthorize();
  const location = useLocation();
  const { orderId } = useParams();
  const { Notify, variants } = useNotify();
  const { branchesForDisplay } = useBranches();
  const { business, branch, pv } = useUser();
  const { order, loading, invoice, voidOrder, paymentsCancelation, refund, updateCustomer, retryOperation, cancelSale, cancelOperation, getCurrentOrder } = useOrder({ orderId });
  const { cashRegister } = useCashRegisterById(order?.cashRegisterId);
  const { warehouses } = useWarehouse({ isExport: true, country: branch.country });

  // Modals
  const [autoPrintInvoice, setAutoPrintInvoice] = useState(null);
  const voucherDetailsProps = useVoucherDetails({ order, autoPrintInvoice });
  const [showModal, setShowModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [orderType, setOrderType] = useState(OrderStates.Refunded);
  const [refundData, setRefundData] = useState();
  const [manualInvoiceNumberData, setManualInvoiceNumberData] = useState();

  const [showExtraData, setShowExtraData] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [showRefundCupon, setShowRefundCupon] = useState(false);
  const [refundAmount, setRefundAmount] = useState(0);
  const [loadingUser, setLoadingUser] = useState(false);

  const [confirmData, setConfirmData] = useState({
    message: "",
    onAccept: () => { },
    onClose: () => { },
    onManual: null
  });

  const history = useNavigate();

  const initInvoice = useCallback(async (manualInvoiceData = null, refundItems = null) => {
    try {
      const order = await invoice(manualInvoiceData, refundItems);
      if (order.state === OrderStates.Invoiced)
        setAutoPrintInvoice(order.vouchers[0]);
      else
        Notify("Ocurrio un error al intentar facturar.", variants.ERROR);
    } catch (err) {
      String(err).includes("AfipException")
        ? Notify("Ocurrió un error al intentar facturar con AFIP, intente más tarde o facture manualmente", variants.ERROR)
        : Notify(err.message, variants.ERROR);
      setShowExtraData(false);
    }
  }, [invoice, Notify, variants]);

  const initRefund = async ({ refundItems, refundCupons, manualInvoiceData }) => {
    try {
      const order = await refund({ refundItems, refundCupons, manualInvoiceData });
      if (order.state !== OrderStates.Refunded && (order.state !== OrderStates.PartiallyRefunded && order.state !== OrderStates.Voided) && order.state !== OrderStates.OperationProcessing)
        Notify("Ocurrio un error al intentar devolver.", variants.ERROR);

      return order;
    } catch (err) {
      Notify(err.message, variants.ERROR);
    }
  }

  const initVoid = async (extraData = {}) => {
    try {
      const order = await voidOrder(extraData);
      if (order.state !== OrderStates.Voided && order.state !== OrderStates.OperationProcessing)
        Notify("Ocurrio un error al intentar realizar una devolucion total.", variants.ERROR);

      return order;
    } catch (err) {
      Notify(err.message, variants.ERROR);
    }
  }

  const NeedExtraData = useCallback((isChange, manualCheck = false, refundAmount = 0) => {
    let show = false;

    // Show manual invoice
    if (order.subChannel === subChannels.values.FacturaManual || manualCheck) {
      setShowManual(true);
      show = true;
    } else setShowManual(false);

    if (!isChange) {
      // show Cupon Refund
      const allCardPayments = getCardPaymentsToRefundFromOrder(order);
      const cardPayments = filterCardPaymentsByRefundAmount(allCardPayments, pv.hasTerminal, refundAmount);

      if (cardPayments.length <= 0 || (pv.hasTerminal && cardPayments.length === 1))
        setShowRefundCupon(false);
      else if (cardPayments.length === 1 && order.payments.length === 1 && refundAmount > 0) {
        setShowRefundCupon(true);
        show = true;
      } else {
        // Si tiene otros pagos y el monto a devolver los supera
        const cashTotal = order.payments
          ?.filter(x => x.paymentMethodType !== paymentMethods.Card && x.paymentMethodType !== paymentMethods.Posnet &&
            (x.state === PaymentStates.Approved || x.state === PaymentStates.PartiallyRefunded))
          .reduce((accumulator, cash) => accumulator + cash.totalAmount, 0) || 0;

        if (refundAmount > cashTotal) {
          setShowRefundCupon(true);
          setRefundAmount(refundAmount);
          show = true;
        }
      }
    }

    setShowExtraData(show); // Mostrar Popup
    return show;
  }, [order.subChannel, order.payments, pv])

  const handleInvoice = useCallback(() =>
    handleActionWithConfirm("Se va a facturar la orden. ¿Confirma?", () => {
      if (order.itemsToReturn && order.itemsToReturn.length > 0) {
        setOrderType(OrderStates.InvoiceInitiated);
        setShowRefundModal(true);
      } else {
        setOrderType(null);
        if (!NeedExtraData())
          initInvoice();
      }
    }),
    [initInvoice, NeedExtraData]);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const needInvoice = query.get("invoice");

    if (needInvoice === "true" && order.state === OrderStates.PaymentProcessed) {
      handleInvoice();
    }
  }, [location.search, order.state, handleInvoice]);

  const handleActionWithConfirm = (message, onAccept, onManual = null) => {
    setConfirmData({
      message,
      onAccept: () => {
        setShowConfirm(false);
        onAccept();
      },
      onManual: onManual
        ? () => {
          setShowConfirm(false)
          onManual();
        }
        : null,
      onClose: () => setShowConfirm(false)
    });

    setShowConfirm(true);
  };

  const handleChange = async () => {
    const currentOrder = await getCurrentOrder()

    // Los cambios sobre Terminal QR los rechaza sales-api por tipo de operación, así que acá
    // no se valida el PV del pago QR.
    const blocker = getTerminalPvBlocker(currentOrder, { pv, business, validateQrBillingCode: false })
    if (blocker) {
      setShowInfoModal(blocker);
      return
    }

    handleActionWithConfirm("¿Está seguro que desea realizar el cambio?", () => {
      setOrderType(OrderStates.Invoiced);
      setShowRefundModal(true);
    }
    );
  }


  const handleRefund = async () => {
    const currentOrder = await getCurrentOrder()

    // Las devoluciones parciales sobre Terminal QR las rechaza sales-api por tipo de operación.
    const blocker = getTerminalPvBlocker(currentOrder, { pv, business, validateQrBillingCode: false })
    if (blocker) {
      setShowInfoModal(blocker);
      return
    }

    handleActionWithConfirm("¿Está seguro que desea realizar una devolución?", () => {
      setOrderType(OrderStates.Refunded);
      setShowRefundModal(true);
    });
  }

  const handleVoid = async () => {
    const currentOrder = await getCurrentOrder()

    const blocker = getTerminalPvBlocker(currentOrder, { pv, business })
    if (blocker) {
      setShowInfoModal(blocker);
      return
    }

    const modalMessage = "¿Está seguro que desea realizar una devolución total?";
    if (order.state === OrderStates.PaymentProcessed) {
      handleActionWithConfirm(modalMessage, initVoid);
    } else
      handleActionWithConfirm(modalMessage, () => {
        setOrderType(OrderStates.Voided);
        setShowRefundModal(true);
      });
  }

  const handleRefundSuccess = async (data, isManual, refundAmount) => {
    setShowRefundModal(false);
    setRefundData(data);

    switch (orderType) {
      case OrderStates.Refunded: // Devolucion Parcial
        if (!NeedExtraData(false, isManual, refundAmount)) {
          const updatedOrder = await initRefund({ refundItems: data }); // Hacer Refund
          onRedirectToRefundView(updatedOrder);
        }
        break;
      case OrderStates.Voided:
        if (!NeedExtraData(false, isManual, order.totalAmount)) {
          const updatedOrder = await initVoid({ refundItems: data });
          onRedirectToRefundView(updatedOrder);
        }
        break;
      case OrderStates.Invoiced:
        if (!NeedExtraData(true)) {
          setManualInvoiceNumberData(data.manualInvoiceData);
          setShowModal(true);
        }
        break;
      case OrderStates.InvoiceInitiated:
        if (!NeedExtraData(false, isManual, order.totalAmount))
          initInvoice(null, data);
        break;
      default:
        break;
    }
  };

  const handleExtraData = async (data) => {
    setShowExtraData(false);

    switch (orderType) {
      case OrderStates.Refunded:
        await initRefund({ ...data, refundItems: refundData });
        break;
      case OrderStates.Invoiced:
        setManualInvoiceNumberData(data.manualInvoiceData);
        setShowModal(true);
        break;
      case OrderStates.Voided:
        initVoid({ ...data, refundItems: refundData });
        break;
      case OrderStates.InvoiceInitiated:
        initInvoice(data.manualInvoiceData, refundData);
        break;
      default:
        initInvoice(data.manualInvoiceData);
        break;
    }
  }

  const editEnabled = (order) =>
    order.branchCode === branch.code &&
    order.state !== OrderStates.Invoiced &&
    order.state !== OrderStates.Voided &&
    order.state !== OrderStates.Refunded &&
    order.state !== OrderStates.PartiallyRefunded;


  // Lleva al usuario a payment-web para resolver los items en la terminal integrada.
  // El modal de PV corta la redirección: sin el PV correcto no puede operar la terminal.
  const redirectToTerminal = (currentOrder) => {
    const blocker = getTerminalPvBlocker(currentOrder, { pv, business })
    if (blocker) {
      setShowInfoModal(blocker);
      return
    }

    onRedirectToRefundView(currentOrder);
  }

  const handleRetryOperation = async () => {
    handleActionWithConfirm("¿Desea reintentar la última operación?", async () => {
      const currentOrder = await retryOperation();

      // El reintento republica el item en la terminal y deja la orden en OperationProcessing:
      // hay que volver a payment-web para que el usuario lo resuelva.
      if (!currentOrder || !HasPendingTerminalItems(currentOrder)) return

      redirectToTerminal(currentOrder);
    });
  }

  const handleCancelSale = async () => {
    handleActionWithConfirm("¿Desea cancelar la venta?", async () => {
      const currentOrder = await cancelSale();

      redirectToTerminal(currentOrder);
    });
  }

  const notifyCancelOperationOutcome = (currentOrder) => {
    if (currentOrder.state === OrderStates.PaymentProcessed) {
      Notify("Se liberaron los items de la terminal integrada y la venta se restauró. No quedó nada pendiente.", variants.SUCCESS);
      return
    }

    if (currentOrder.state === OrderStates.OperationRejected) {
      Notify("Se liberaron los items de la terminal integrada, pero la devolución quedó interrumpida. Reintentá la operación desde la orden.", variants.WARNING);
      return
    }

    Notify("Se liberaron los items pendientes en la terminal integrada.", variants.INFO);
  }

  const handleCancelOperation = async () => {
    handleActionWithConfirm("Se liberarán los items pendientes en la terminal integrada. Según lo que ya se haya procesado, la venta podrá restaurarse o la devolución quedará pendiente de reintento. ¿Confirma?", async () => {
      const currentOrder = await cancelOperation();

      // cancelOperation notifica el error y devuelve undefined si falló.
      if (!currentOrder) return

      notifyCancelOperationOutcome(currentOrder);

      // Solo volvemos a la terminal si todavía quedó algo publicado en ella.
      // En la liberación parcial la operación queda en OperationRejected (nada pendiente
      // en la terminal), así que redirigir mostraría una pantalla vacía del item liberado.
      if (!HasPendingTerminalItems(currentOrder)) return

      redirectToTerminal(currentOrder);
    });
  }

  const handleUpdateCustomerSuccess = (customer) => {
    updateCustomer(customer);
    Notify("Cliente actualizado correctamente", variants.SUCCESS);
  }

  //   <CustomerDetails
  //   customer={order.customer}
  //   editEnabled={editEnabled(order)}
  //   orderId={order.id}
  //   onSuccess={handleUpdateCustomer}
  //   onError={() => Notify("No se pudo actualizar el cliente", variants.ERROR)}
  // />

  const handleUpdateCustomer = async () => {
    setLoadingUser(true);
    try {
      const updateCustomer = await customerService.Get(order.customer.id);
      await orderService.UpdateCustomer(orderId, updateCustomer);
      handleUpdateCustomerSuccess(updateCustomer);
    } catch (err) {
      Notify("No se pudo actualizar el cliente", variants.ERROR);
    } finally {
      setLoadingUser(false);
    }
  }

  return (
    <Grid container spacing={1}>
      {loadingUser && (
        <div>
          <Spinner lockScreen />
        </div>
      )}
      {showInfoModal && showInfoModal === "InvalidPvForTerminalQr" &&
        <TerminalQrPvModal
          open={showInfoModal}
          billingCode={order.billingCode}
          branchCode={order.branchCode}
          onAccept={() => setShowInfoModal(false)}
        />
      }
      {showInfoModal && showInfoModal === "InvalidPvForTerminal" &&
        <TerminalPvModal
          open={showInfoModal}
          onAccept={() => setShowInfoModal(false)}
        />
      }
      {showExtraData &&
        <ExtraDataModal
          open={showExtraData}
          onSave={handleExtraData}
          onClose={() => setShowExtraData(false)}
          order={order}
          showManual={showManual}
          showRefundCupon={showRefundCupon}
          showPv
          showCreditNote={order.refund && order.refund > 0 && order.vouchers.length === 0}
          refundAmount={refundAmount}
        />}
      {showModal &&
        <NewSaleModal
          open={showModal}
          orderId={order.id}
          onClose={() => setShowModal(false)}
          refundItems={refundData}
          manualInvoiceNumber={manualInvoiceNumberData}
        />}
      {((showRefundModal && order.round === 0) || (showRefundModal && (orderType === OrderStates.InvoiceInitiated || orderType === OrderStates.Voided))) &&
        <RefundModal
          open={showRefundModal}
          onSucces={handleRefundSuccess}
          onClose={() => setShowRefundModal(false)}
          order={order}
          openMode={orderType}
          setRefund={setRefundData}
        />}
      {showConfirm &&
        <ModalManualConfirm
          open={showConfirm}
          message={confirmData.message}
          onAccept={confirmData.onAccept}
          onManual={confirmData.onManual}
          onCancel={confirmData.onClose}
          onClose={confirmData.onClose}
        />}
      <Grid size={12}>
        <TitleDetail
          order={order}
          loading={loading}
          onVoid={handleVoid}
          onRefund={handleRefund}
          onInvoice={handleInvoice}
          onChangeClick={handleChange}
          onInternalVoucher={() => history(`/internalVouchers/create`, { state: { order } })}
          onRetryOperation={handleRetryOperation}
          onCancel={handleCancelSale}
          onCancelOperation={handleCancelOperation}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 4,
          xl: 3
        }}
      >
        <Masonry columns={{ xs: 1 }} spacing={1}>
          {order && (
            <GeneralDetail order={order} branchesForDisplay={branchesForDisplay} cashRegister={cashRegister} />
          )}
          {order && (
            <TotalsDetail order={order} />
          )}
          {order?.customer && (
            <CardContact
              customer={order.customer}
              editEnabled={editEnabled(order)}
              isAuthorized={isAuthorized([Permissions.SalesUpdateCustomer])}
              handleUpdateCustomer={handleUpdateCustomer}
            />
          )}
          {order?.shippingData && (
            <ShippingDetails shippingData={order.shippingData} branches={branchesForDisplay} warehouses={warehouses} order={order} />
          )}
          {order?.observations && (
            <ObservationsDetails value={order.observations} title="Observaciones" />
          )}
          {(order?.originalOrderId || (order?.newOrderIds && !!order?.newOrderIds?.length)) && (
            <RelatedOrderDetail originalOrderId={order.originalOrderId} newOrderIds={order.newOrderIds} />
          )}
        </Masonry>
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 8,
          xl: 9
        }}
      >
        <Masonry columns={{ xs: 1, md: 1 }} spacing={1}>
          {order?.items && (
            <ProductsDetail title='PRODUCTOS' items={order.items} isReturnList={false} currency={order.currency} warehouses={warehouses} />
          )}
          {order?.itemsToReturn && !!order?.itemsToReturn.length && (
            <ProductsDetail title='PRODUCTOS DEVUELTOS' items={order.itemsToReturn} isReturnList currency={order.currency} warehouses={warehouses} />
          )}
        </Masonry>
        <Masonry columns={{ xs: 1, xl: 2, md: 1 }} spacing={1}>
          {order?.payments && !!order?.payments.length && (
            <PaymentDetails order={order} onPaymentsCancelation={paymentsCancelation} />
          )}
          {(order?.items?.some(item => item.promotions?.length > 0) || order?.additionalPromotions?.length > 0) && (
            <PromotionsDetail items={order?.items} currency={order?.currency} />
          )}
          {order?.additionalPromotions?.length > 0 && (
            <PromoAddition additions={order?.additionalPromotions} items={order?.items} displayMode='applied' />
          )}
          {order?.vouchers?.length > 0 && (
            <VoucherDetails {...voucherDetailsProps} />
          )}
          {order?.internalVouchers?.length > 0 && (
            <InternalVoucherDetails
              vouchers={order?.internalVouchers}
              getVoucherLink={(voucher) => `/internalVouchers/detail/${voucher.id}`}
            />
          )}
        </Masonry>
      </Grid>
    </Grid>
  );
};

export default DetailView;
